import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { ISystemConfig, IColumnConfig, IPriorityConfig, ICategoryConfig, IDemandsConfig, IGeneralConfig, IReportsConfig } from '@/types';
import { apiService } from '@services/api';
import logger from '@utils/logger';
import { getFriendlyErrorMessage } from '@utils/getFriendlyErrorMessage';
import { COLORS, ROUTES } from '@/constants';
import { useStableCallback } from '@hooks/useStableCallback';

interface ConfigContextType {
  config: ISystemConfig | null;
  loading: boolean;
  error: string | null;
  // Demandas
  updateDemandColumns: (columns: IColumnConfig[]) => Promise<void>;
  updateDemandPriorities: (priorities: IPriorityConfig[]) => Promise<void>;
  updateDemandCategories: (categories: ICategoryConfig[]) => Promise<void>;
  updateDemandsConfig: (demandsConfig: IDemandsConfig) => Promise<void>;
  // Configurações Gerais
  updateGeneralConfig: (general: IGeneralConfig) => Promise<void>;
  // Relatórios
  updateReportsConfig: (reports: IReportsConfig) => Promise<void>;
  // Utilitários
  refreshConfig: () => Promise<void>;
  refreshDemandsConfig: () => Promise<void>;
  getDemandsConfig: () => IDemandsConfig | null;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

// Configurações padrão (fallback se API falhar)
const defaultConfig: ISystemConfig = {
  general: {},
  demandManagement: {
    columns: [
      { id: '1', name: 'Aberta', key: 'pending', color: COLORS.status.pending, order: 1, isActive: true },
      {
        id: '2', name: 'Em Andamento', key: 'in_progress', color: COLORS.status.inProgress, order: 2, isActive: true,
        // Mesmo raciocínio do fallback de 'cancelled' logo abaixo: sem isso, sob o timeout de 3s
        // do Promise.race, o encaminhamento a prestador ao mover pra "Em Andamento" sumia mesmo
        // com o tenant configurado corretamente pra exibi-lo. Espelha signupDefaults.ts.
        showProviderForwardOnEnter: true,
      },
      { id: '3', name: 'Concluída', key: 'completed', color: COLORS.status.completed, order: 3, isActive: true },
      {
        id: '4', name: 'Cancelada', key: 'cancelled', color: COLORS.status.cancelled, order: 4, isActive: true,
        // Achado real: a busca de config tem timeout de 3s (Promise.race abaixo) e cai
        // silenciosamente neste fallback sob latência — sem esses dois campos aqui, "editar pra
        // Cancelada" parava de pedir o motivo sempre que esse timeout batia (mesmo com o tenant
        // configurado corretamente no banco), enquanto o drag-and-drop parecia funcionar por pura
        // sorte de timing. Espelha o default do backend (signupDefaults.ts).
        requireObservationOnEnter: true,
        observationRequiredOnEnter: true,
      },
    ],
    priorities: [
      { id: '1', name: 'Baixa', key: 'baixa', color: COLORS.severity.baixa, order: 1, isActive: true },
      { id: '2', name: 'Média', key: 'media', color: COLORS.severity.media, order: 2, isActive: true },
      { id: '3', name: 'Alta', key: 'alta', color: COLORS.severity.alta, order: 3, isActive: true },
      { id: '4', name: 'Urgente', key: 'urgente', color: COLORS.severity.urgent, order: 4, isActive: true },
    ],
    categories: [
      { id: '1', name: 'Manutenção', key: 'manutencao', color: COLORS.category.manutencao, order: 1, isActive: true },
      { id: '2', name: 'Limpeza', key: 'limpeza', color: COLORS.category.limpeza, order: 2, isActive: true },
      { id: '3', name: 'Segurança', key: 'seguranca', color: COLORS.category.seguranca, order: 3, isActive: true },
      { id: '4', name: 'Infraestrutura', key: 'infraestrutura', color: COLORS.category.infraestrutura, order: 4, isActive: true },
      { id: '5', name: 'Áreas Comuns', key: 'areas_comuns', color: COLORS.category.areasComuns, order: 5, isActive: true },
      { id: '6', name: 'Outros', key: 'outros', color: COLORS.category.outros, order: 6, isActive: true },
    ],
    applicantTypes: [
      { id: '1', name: 'Morador', key: 'morador', order: 1, isActive: true },
    ],
    defaultPeriod: 'all',
  },
  reports: {
    defaultPeriod: 'month',
    exportFormats: ['pdf', 'excel'],
    performanceThresholds: {
      excellent: 3,
      good: 7,
      regular: 14
    },
    severityThresholds: {
      critical: 5,
      attention: 3,
      monitor: 2,
      criticalColor: '#f44336',
      attentionColor: '#ff9800',
      monitorColor: '#2196f3'
    },
  },
  updatedAt: new Date(),
  version: '1.0.0',
};

export const ConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<ISystemConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Carregar todas as configurações da API com timeout curto
      // Se o backend não estiver disponível, usar configurações padrão silenciosamente
      const response = await Promise.race([
        apiService.getAllConfig(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('TIMEOUT')), 3000)
        )
      ]);
      
      if (response?.data?.success && response.data.data) {
        const apiConfig = response.data.data;
        
        // Montar configuração no formato esperado
        // Garantir que severityThresholds tenha cores padrão se não existirem
        const reportsConfig = apiConfig.reports || defaultConfig.reports;
        if (reportsConfig.severityThresholds) {
          reportsConfig.severityThresholds = {
            ...defaultConfig.reports.severityThresholds,
            ...reportsConfig.severityThresholds,
            // Garantir que cores existam
            criticalColor: reportsConfig.severityThresholds.criticalColor || defaultConfig.reports.severityThresholds.criticalColor || '#f44336',
            attentionColor: reportsConfig.severityThresholds.attentionColor || defaultConfig.reports.severityThresholds.attentionColor || '#ff9800',
            monitorColor: reportsConfig.severityThresholds.monitorColor || defaultConfig.reports.severityThresholds.monitorColor || '#2196f3',
          };
        }
        
        const dm = apiConfig.demandManagement || defaultConfig.demandManagement;
        const defaultApplicantTypes = defaultConfig.demandManagement?.applicantTypes || [
          { id: '1', name: 'Morador', key: 'morador', order: 1, isActive: true },
        ];
        const applicantTypes = Array.isArray(dm?.applicantTypes) && dm.applicantTypes.length > 0
          ? dm.applicantTypes
          : defaultApplicantTypes;
        const systemConfig: ISystemConfig = {
          general: apiConfig.general || defaultConfig.general,
          demandManagement: { ...dm, applicantTypes },
          reports: reportsConfig,
          updatedAt: apiConfig.updatedAt ? new Date(apiConfig.updatedAt) : new Date(),
          version: apiConfig.version || '1.0.0',
        };
        
        setConfig(systemConfig);
      } else {
        setConfig(defaultConfig); // API retornou erro ou estrutura inesperada
      }
      
    } catch (rawErr: unknown) {
      const err = rawErr as { response?: { status?: number }; code?: string; message?: string };
      // Verificar se é um erro de autenticação (401) - esperado quando não há token
      const isUnauthorized = err.response?.status === 401;

      // Verificar se é um erro de rede ou timeout (backend não disponível)
      const isNetworkError = err.code === 'ERR_NETWORK' ||
                            err.code === 'ERR_SOCKET_NOT_CONNECTED' ||
                            err.code === 'ERR_CONNECTION_RESET' ||
                            err.message === 'TIMEOUT' ||
                            err.message?.includes('Network Error') ||
                            err.message?.includes('ECONNREFUSED') ||
                            err.message?.includes('timeout');
      
      if (isUnauthorized || isNetworkError) {
        // Erro de autenticação ou rede - usar configurações padrão silenciosamente
        // Não propagar o erro para evitar que apareça no console
        setConfig(defaultConfig);
        // Não logar nada - erro silencioso
      } else {
        // Outro tipo de erro - apenas definir o estado, sem logar
        setError(getFriendlyErrorMessage(err, 'Erro ao carregar configurações'));
        setConfig(defaultConfig);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateDemandColumns = async (columns: IColumnConfig[]) => {
    if (!config) {
      return;
    }
    
    try {
      const sortedColumns = columns.sort((a, b) => a.order - b.order);
      
      // Atualizar via API
      const response = await apiService.updateDemandColumns({ columns: sortedColumns });
      
      if (response.data.success && response.data.data) {
        const apiConfig = response.data.data;
        
        // Atualizar estado local
        const updatedConfig: ISystemConfig = {
          ...config,
          demandManagement: {
            ...config.demandManagement,
            columns: apiConfig.columns || sortedColumns,
          },
          updatedAt: new Date(),
        };
        
        setConfig(updatedConfig);
      }
    } catch (err) {
      logger.error('Erro ao atualizar colunas:', err);
      throw err;
    }
  };

  const updateDemandPriorities = async (priorities: IPriorityConfig[]) => {
    if (!config) return;
    
    try {
      const sortedPriorities = priorities.sort((a, b) => a.order - b.order);
      
      // Atualizar via API
      const response = await apiService.updateDemandPriorities({ priorities: sortedPriorities });
      
      if (response.data.success && response.data.data) {
        const apiConfig = response.data.data;
        
        // Atualizar estado local
        const updatedConfig = {
          ...config,
          demandManagement: {
            ...config.demandManagement,
            priorities: apiConfig.priorities || sortedPriorities,
          },
          updatedAt: new Date(),
        };
        
        setConfig(updatedConfig);
      }
    } catch (err) {
      logger.error('Erro ao atualizar colunas:', err);
      throw err;
    }
  };

  const updateDemandCategories = async (categories: ICategoryConfig[]) => {
    if (!config) return;
    
    try {
      // Ordenar categorias alfabeticamente por nome
      const sortedCategories = categories.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      
      // Atualizar via API
      const response = await apiService.updateDemandCategories({ categories: sortedCategories });
      
      if (response.data.success && response.data.data) {
        const apiConfig = response.data.data;
        
        // Atualizar estado local
        const updatedConfig = {
          ...config,
          demandManagement: {
            ...config.demandManagement,
            categories: apiConfig.categories || sortedCategories,
          },
          updatedAt: new Date(),
        };
        
        setConfig(updatedConfig);
      }
    } catch (err) {
      logger.error('Erro ao atualizar colunas:', err);
      throw err;
    }
  };

  const updateDemandsConfig = async (demandsConfig: IDemandsConfig) => {
    if (!config) return;
    
    try {
      // Atualizar via API
      const response = await apiService.updateDemandsConfig(demandsConfig);
      
      if (response.data.success && response.data.data) {
        const apiDemandsConfig = response.data.data;
        
        // Atualizar estado local
        const updatedConfig = {
          ...config,
          demandManagement: apiDemandsConfig,
          updatedAt: new Date(),
        };
        
        setConfig(updatedConfig);
      }
    } catch (err) {
      logger.error('Erro ao atualizar colunas:', err);
      throw err;
    }
  };

  const updateGeneralConfig = async (general: IGeneralConfig) => {
    if (!config) return;
    
    try {
      // Atualizar via API
      const response = await apiService.updateGeneralConfig(general);
      
      if (response.data.success && response.data.data) {
        const apiGeneral = response.data.data;
        
        // Atualizar estado local
        const updatedConfig = {
          ...config,
          general: apiGeneral,
          updatedAt: new Date(),
        };
        
        setConfig(updatedConfig);
      }
    } catch (err) {
      logger.error('Erro ao atualizar colunas:', err);
      throw err;
    }
  };

  const updateReportsConfig = async (reports: IReportsConfig) => {
    if (!config) return;
    
    try {
      // Atualizar via API
      const response = await apiService.updateReportsConfig(reports);
      
      if (response.data.success && response.data.data) {
        const apiReports = response.data.data;
        
        // Atualizar estado local
        const updatedConfig = {
          ...config,
          reports: apiReports,
          updatedAt: new Date(),
        };
        
        setConfig(updatedConfig);
      }
    } catch (err) {
      logger.error('Erro ao atualizar colunas:', err);
      throw err;
    }
  };

  const getDemandsConfig = (): IDemandsConfig | null => {
    return config?.demandManagement || null;
  };

  const refreshConfig = async () => {
    await loadConfig();
  };

  const refreshDemandsConfig = async () => {
    if (!config) return;
    try {
      const res = await apiService.getDemandsConfig();
      if (res?.data?.success && res.data.data) {
        const dm = res.data.data as IDemandsConfig;
        const applicantTypes = Array.isArray(dm?.applicantTypes) && dm.applicantTypes.length > 0
          ? dm.applicantTypes
          : (defaultConfig.demandManagement?.applicantTypes || [{ id: '1', name: 'Morador', key: 'morador', order: 1, isActive: true }]);
        setConfig((prev) => prev ? {
          ...prev,
          demandManagement: { ...dm, applicantTypes },
          updatedAt: new Date(),
        } : prev);
      }
    } catch {
      // Falha ao recarregar config de demandas: manter estado atual
    }
  };

  useEffect(() => {
    const path = window.location.pathname;
    const isPublicPath =
      path === ROUTES.HOME ||
      path === '/home' ||
      path === ROUTES.LOGIN ||
      path === ROUTES.UNAUTHORIZED ||
      path.startsWith(ROUTES.PUBLIC_DEMAND);

    if (isPublicPath) {
      setConfig(defaultConfig);
      setLoading(false);
    } else {
      // Carregar configuração; se não houver sessão válida, backend retornará 401 e usaremos config padrão
      loadConfig();
    }

    // Escutar eventos customizados de login/logout
    const handleLogin = () => loadConfig();
    const handleLogout = () => {
      setConfig(defaultConfig);
    };
    
    window.addEventListener('login', handleLogin);
    window.addEventListener('logout', handleLogout);
    
    return () => {
      window.removeEventListener('login', handleLogin);
      window.removeEventListener('logout', handleLogout);
    };
  }, []);

  // Identidade estável entre renders — mesma proteção aplicada em AuthContext/DemandsContext
  // contra loops de efeito causados por funções de contexto recriadas a cada render.
  const stableUpdateDemandColumns = useStableCallback(updateDemandColumns);
  const stableUpdateDemandPriorities = useStableCallback(updateDemandPriorities);
  const stableUpdateDemandCategories = useStableCallback(updateDemandCategories);
  const stableUpdateDemandsConfig = useStableCallback(updateDemandsConfig);
  const stableUpdateGeneralConfig = useStableCallback(updateGeneralConfig);
  const stableUpdateReportsConfig = useStableCallback(updateReportsConfig);
  const stableRefreshConfig = useStableCallback(refreshConfig);
  const stableRefreshDemandsConfig = useStableCallback(refreshDemandsConfig);
  const stableGetDemandsConfig = useStableCallback(getDemandsConfig);

  const value = useMemo<ConfigContextType>(() => ({
    config,
    loading,
    error,
    updateDemandColumns: stableUpdateDemandColumns,
    updateDemandPriorities: stableUpdateDemandPriorities,
    updateDemandCategories: stableUpdateDemandCategories,
    updateDemandsConfig: stableUpdateDemandsConfig,
    updateGeneralConfig: stableUpdateGeneralConfig,
    updateReportsConfig: stableUpdateReportsConfig,
    refreshConfig: stableRefreshConfig,
    refreshDemandsConfig: stableRefreshDemandsConfig,
    getDemandsConfig: stableGetDemandsConfig,
  }), [
    config, loading, error,
    stableUpdateDemandColumns, stableUpdateDemandPriorities, stableUpdateDemandCategories,
    stableUpdateDemandsConfig, stableUpdateGeneralConfig, stableUpdateReportsConfig,
    stableRefreshConfig, stableRefreshDemandsConfig, stableGetDemandsConfig
  ]);

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};
